/**
 * Configuração e validação do cadastro guiado de grupo de grade.
 *
 * A separação entre erro e aviso é a regra do fluxo: **erro** é o que o backend
 * (App\Http\Requests\GrupoGradeRequest) rejeitaria, então trava o avanço;
 * **aviso** é recomendação e nunca trava — só aparece para o supervisor decidir.
 */

export const LIMITE_CAMPOS = 5;

export const ETAPAS = [
  {
    numero: 1,
    titulo: "Identificação",
    resumo: "Nome e situação",
  },
  {
    numero: 2,
    titulo: "Quais produtos entram",
    resumo: "Condições de classificação",
  },
  {
    numero: 3,
    titulo: "Como o cliente escolhe",
    resumo: "Variações e ordenação",
  },
  {
    numero: 4,
    titulo: "Revisão",
    resumo: "Conferir e salvar",
  },
];

export const ULTIMA_ETAPA = ETAPAS.length;

function regrasDo(form) {
  return form.regras || [];
}

function camposDo(form) {
  return form.campos || [];
}

function temDuplicadas(lista) {
  const preenchidas = lista.filter(Boolean);
  return preenchidas.length !== new Set(preenchidas).size;
}

function validarIdentificacao(form) {
  const erros = [];
  const avisos = [];

  const nome = (form.nome || "").trim();

  if (!nome) {
    erros.push("Informe o nome do grupo.");
  } else if (nome.length > 120) {
    erros.push("O nome deve ter no máximo 120 caracteres.");
  }

  if ((form.descricao || "").length > 500) {
    erros.push("A descrição deve ter no máximo 500 caracteres.");
  }

  if (nome && !(form.descricao || "").trim()) {
    avisos.push(
      "Sem descrição, quem abrir este grupo depois não vai saber que categoria ele cobre."
    );
  }

  return { erros, avisos };
}

function validarProdutos(form, diagnostico) {
  const erros = [];
  const avisos = [];
  const regras = regrasDo(form);

  if (!regras.length) {
    erros.push("Adicione pelo menos uma condição — sem condição nenhum produto entra no grupo.");
    return { erros, avisos };
  }

  regras.forEach((regra, indice) => {
    if (!regra.colunaProduto) {
      erros.push(`Condição ${indice + 1}: escolha o atributo do produto.`);
    }
    if (!regra.valor || !String(regra.valor).trim()) {
      erros.push(`Condição ${indice + 1}: informe o valor.`);
    }
  });

  if (temDuplicadas(regras.map((regra) => regra.colunaProduto))) {
    erros.push("Cada atributo pode ter só uma condição — remova a duplicada.");
  }

  if (diagnostico && diagnostico.excede_limite) {
    erros.push(
      `A maior grade gerada teria ${diagnostico.maior_grade} SKUs, acima do limite de ` +
        `${diagnostico.limite_permitido}. Refine as condições para separar em grades menores.`
    );
  }

  if (diagnostico && !erros.length) {
    if (!diagnostico.total_produtos) {
      avisos.push(
        "Nenhum produto casa com essas condições hoje. Você pode salvar assim, mas a grade " +
          "só aparecerá quando existirem produtos com esses atributos."
      );
    }

    if (diagnostico.total_sem_agrupador) {
      avisos.push(
        `${diagnostico.total_sem_agrupador} produto(s) casam com as condições mas estão sem ` +
          "agrupador de grade e continuarão sem grade."
      );
    }
  }

  return { erros, avisos };
}

function validarVariacoes(form, colunasDisponiveis = []) {
  const erros = [];
  const avisos = [];
  const campos = camposDo(form);

  if (!campos.length) {
    erros.push(
      "Adicione pelo menos uma variação — é ela que dá ao cliente as opções dentro da grade."
    );
    return { erros, avisos };
  }

  if (campos.length > LIMITE_CAMPOS) {
    erros.push(`No máximo ${LIMITE_CAMPOS} variações por grupo.`);
  }

  campos.forEach((campo, indice) => {
    if (!campo.colunaProduto) {
      erros.push(`Variação ${indice + 1}: escolha o atributo do produto.`);
      return;
    }

    if (campo.ordenacao === "manual" && !(campo.ordemValores || []).length) {
      const opcao = colunasDisponiveis.find((item) => item.coluna === campo.colunaProduto);
      const nome = campo.labelCustomizado || (opcao ? opcao.label : `Variação ${indice + 1}`);
      avisos.push(
        `"${nome}" está com ordenação manual sem nenhum valor posicionado e vai cair na ordem automática.`
      );
    }
  });

  if (temDuplicadas(campos.map((campo) => campo.colunaProduto))) {
    erros.push("Cada atributo pode aparecer só uma vez como variação — remova a duplicada.");
  }

  return { erros, avisos };
}

/**
 * Valida uma etapa isolada.
 *
 * @returns {{erros: string[], avisos: string[]}}
 */
export function validarEtapa(numero, form, diagnostico, colunasDisponiveis) {
  if (numero === 1) return validarIdentificacao(form);
  if (numero === 2) return validarProdutos(form, diagnostico);
  if (numero === 3) return validarVariacoes(form, colunasDisponiveis);

  // A revisão não tem campo próprio: ela consolida as etapas anteriores.
  return { erros: [], avisos: [] };
}

/**
 * Valida o formulário inteiro, devolvendo o resultado por etapa e o consolidado.
 *
 * @returns {{porEtapa: Object, erros: string[], avisos: string[], valido: boolean}}
 */
export function validarFormulario(form, diagnostico, colunasDisponiveis) {
  const porEtapa = {};
  let erros = [];
  let avisos = [];

  ETAPAS.forEach(({ numero }) => {
    const resultado = validarEtapa(numero, form, diagnostico, colunasDisponiveis);
    porEtapa[numero] = resultado;
    erros = erros.concat(resultado.erros);
    avisos = avisos.concat(resultado.avisos);
  });

  return { porEtapa, erros, avisos, valido: erros.length === 0 };
}
